<script setup lang="ts">
import { RequestReadReviews } from '@yescapa-dev/ysc-api-js/modern'
import type { AppReviewListItem } from '@yescapa-dev/ysc-api-js/modern'
import { DEFAULT_COMMUNITY_PAGE_SIZE } from '~/constants/community'
import { YSC_API_REVIEW_ERROR } from '~/utils/error/YscErrorClasses'

definePageMeta({
  middleware: ['force-page-one-if-empty'],
})

const { localeProperties: { yscCode } } = useLocaleProperties()
const route = useRoute()
const { t } = useI18n()
useSeoText({ title: t('pages.community_pictures_wall.meta.title'), description: t('pages.community_pictures_wall.meta.description') })

const page = ref(queryValueToNumber(route.query.page) || 1)

const { $apiConsumer } = useNuxtApp()
const { withErrorManagerHandling } = useWithErrorManagerHandling()
const { status, data: reviews } = await useLazyAsyncData(`page-pictures-wall-${page.value}`, async () => {
  scrollToTop()

  try {
    return withErrorManagerHandling(
      () => $apiConsumer.send(new RequestReadReviews({
        query: {
          language: yscCode,
          has_photos: true,
          page_size: DEFAULT_COMMUNITY_PAGE_SIZE,
          page: page.value,

        },
      })),
      YSC_API_REVIEW_ERROR,
      true,
    )
  }
  catch (_) {
    throw showError({ statusCode: 404 })
  }
}, { watch: [route] })

const { isPending } = useIsPending(status)

const modalData = ref<AppReviewListItem | null>(null)
const onOpenModal = (modalDataToOpen: AppReviewListItem) => {
  modalData.value = modalDataToOpen
}

const onCloseModal = () => {
  modalData.value = null
}
</script>

<template>
  <div>
    <AppPageSection>
      <AppLoading v-if="isPending" />

      <div class="mb-12 grid grid-cols-2 gap-4 md:grid-cols-4">
        <div
          v-for="review of reviews?.results"
          :key="review.id"
          class="cursor-pointer overflow-hidden rounded-lg bg-gray-200"
          @click="onOpenModal(review)"
        >
          <TwicImg
            v-if="!!review.photos[0]"
            :src="twicpicsPath(review.photos[0].path)"
            :alt="`${review.text} - Yescapa`"
          />
        </div>
      </div>

      <div
        v-if="reviews?.count && reviews?.count > DEFAULT_COMMUNITY_PAGE_SIZE"
        class="mb-12"
      >
        <YscPagination
          :page-size="DEFAULT_COMMUNITY_PAGE_SIZE"
          :count="reviews.count"
        />
      </div>
    </AppPageSection>

    <AppSearchSection />

    <AppModalReviewDetails
      v-if="modalData"
      :user-name="modalData.author.first_name"
      :photos="modalData.photos"
      @close="onCloseModal()"
    />
  </div>
</template>
